// src/components/parts/Login.js
import React, { useState } from 'react'; 
import { authService } from '../services/auth'; // Import the service
import '../style/Login.css';

const Login = () => {
  // --- STATE ---
  const [view, setView] = useState('login'); // 'login', '2fa', 'forgot'
  
  // Login Credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // 2FA State
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempUserId, setTempUserId] = useState(null);
  
  // Optimization: Store last successful creds
  const [lastSuccessCreds, setLastSuccessCreds] = useState({ user: '', pass: '' });

  // Forgot Password State
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  // UI States
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false); 

  // --- STEP 1: SEND CREDENTIALS ---
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    // Optimization: Don't re-fetch if just moving back/forth with same creds
    if (tempUserId && username === lastSuccessCreds.user && password === lastSuccessCreds.pass) {
      setView('2fa');
      return;
    }

    setIsLoading(true);

    try {
      // Use authService instead of raw fetch
      const data = await authService.login(username.trim(), password.trim());

      if (data.requires2FA) {
        setTempUserId(data.userId);
        setLastSuccessCreds({ user: username, pass: password }); 
        setView('2fa');             
        setError(''); 
      } else {
        // Should not happen based on your backend logic, but good safety
        handleLoginSuccess(data);
      }

    } catch (err) {
      setError(err.message);
      setTempUserId(null); 
    } finally {
      setIsLoading(false);
    }
  };

  // --- STEP 2: VERIFY CODE ---
  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!twoFactorCode.trim()) {
      setError('Please enter the verification code');
      setIsLoading(false);
      return;
    }

    try {
      // Use authService
      const data = await authService.verify2FA(tempUserId, twoFactorCode.trim());
      handleLoginSuccess(data);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // --- STEP 3: FORGOT PASSWORD REQUEST ---
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setForgotMessage('');
    setIsLoading(true);

    if (!forgotEmail.trim()) {
      setError('Please enter your email address');
      setIsLoading(false);
      return;
    }

    try {
      // Use authService
      await authService.forgotPassword(forgotEmail.trim());
      setForgotMessage('If an account exists with this email, a reset link has been sent.');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // --- SUCCESS HANDLER ---
  const handleLoginSuccess = (data) => {
    // 1. Save Token
    localStorage.setItem('token', data.token);
    
    // 2. CRITICAL: Save Session ID (Required for Logout/Revoke to work)
    if (data.sessionId) {
      localStorage.setItem('sessionId', data.sessionId);
    }

    // 3. Save User Data
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      
      const role = data.user.role_key || data.user.role;
      if (role) localStorage.setItem('userRole', role);
    }

    // 4. Redirect
    window.location.href = '/';
  };

  return (
    <div className="login-container">
      
      <div className="login-form">
        <h1>MyBrandz Document Dashboard</h1>
        
        {/* --- VIEW: LOGIN CREDENTIALS --- */}
        {view === 'login' && (
          <>
            <p className="login-subtitle">Please sign in to continue</p>
            <form onSubmit={handleCredentialsSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username or Email</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username or email" 
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <div style={{ textAlign: 'right', marginTop: '5px' }}>
                  <span 
                    onClick={() => { setView('forgot'); setError(''); }}
                    style={{ fontSize: '0.85rem', color: '#92c04f', cursor: 'pointer' }}
                  >
                    Forgot Password?
                  </span>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Checking...' : 'Next'}
              </button>
            </form>
          </>
        )}

        {/* --- VIEW: 2FA --- */}
        {view === '2fa' && (
          <>
            <p className="login-subtitle">Enter the 6-digit code sent to your email</p>
            <form onSubmit={handle2FASubmit}>
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  id="code"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder="123456"
                  maxLength="6"
                  style={{ letterSpacing: '5px', textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' }}
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify & Login'}
              </button>
              
              <button 
                type="button" 
                className="text-button" 
                onClick={() => {
                  setView('login');
                  setError('');
                }}
                style={{ marginTop: '15px', background: 'none', border: 'none', color: '#666', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to Login
              </button>
            </form>
          </>
        )}

        {/* --- VIEW: FORGOT PASSWORD --- */}
        {view === 'forgot' && (
          <>
            <p className="login-subtitle">Reset your password</p>
            {!forgotMessage ? (
              <form onSubmit={handleForgotSubmit}>
                <div className="form-group">
                  <label htmlFor="forgotEmail">Enter your email address</label>
                  <input
                    id="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="name@example.com"
                    disabled={isLoading}
                  />
                </div>

                {error && <div className="error-message">{error}</div>}

                <button type="submit" className="login-button" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            ) : (
              <div style={{ textAlign: 'center', color: 'green', marginBottom: '20px' }}>
                {forgotMessage}
              </div>
            )}

            <button 
              type="button" 
              className="text-button" 
              onClick={() => {
                setView('login');
                setError('');
                setForgotMessage('');
              }}
              style={{ marginTop: '15px', background: 'none', border: 'none', color: '#666', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Back to Login
            </button>
          </>
        )}
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '10px', 
            marginTop: '20px' 
        }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Powered by</span>
            <img 
                src="https://mybrandz.com.au/wp-content/uploads/2025/04/MyBrandz-logo-2024_med-e1743462702599.png" 
                alt="MyBrandz Logo" 
                style={{ maxWidth: '50px', height: 'auto' }} 
            />
        </div>
      </div>
    </div>
  );
};

export default Login;
